import { useQuery } from '@tanstack/react-query'
import type { LockboxExpandableRowProps, LockboxTransactionsResponse } from '../types'
import $api from '../../../../../API'
import { Skeleton, Table } from 'antd'
import { useMemo } from 'react'
import type { ColumnsType } from 'antd/es/table'
import * as constants from '../constants'
import cx from 'classnames'
import dayjs from 'dayjs'
import type { LockboxTransaction } from '../../../../../domain/lockbox'

export const LockboxExpandableRow = (props: LockboxExpandableRowProps): JSX.Element => {
  const lockboxTransactionsQuery = useQuery({
    queryKey: ['lockbox-transactions', { lockboxId: props.lockboxId }],

    async queryFn({ queryKey, signal }) {
      const { lockboxId } = queryKey[1] as typeof queryKey extends Array<string | infer T>
        ? T
        : never

      const response = await $api.get<LockboxTransactionsResponse>(
        `/pinnacle/lockboxes/${lockboxId}/transactions`,
        {
          signal
        }
      )

      return response.data
    }
  })

  const transactions = lockboxTransactionsQuery.data?.data ?? []

  const columns = useMemo<ColumnsType<LockboxTransaction>>(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 300,

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Deposit Date',
        dataIndex: 'date',
        width: 100,

        render(value: string) {
          return dayjs(value).format('MM/DD/YYYY')
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        align: 'right',

        render(value: number) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(value)
        },

        onCell() {
          return {
            className: cx(constants.baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(constants.baseHeaderCellClassName)
          }
        }
      }
    ],
    []
  )

  return (
    <div className='relative !p-0'>
      <div className='absolute top-0 left-0 z-10 w-1 h-full bg-blue-200' />

      <div className='p-4'>
        <Table
          size='small'
          rowKey='id'
          className='border-2 border-dashed border-blue-200 [&_.ant-table]:![margin-inline:_0]'
          columns={
            !lockboxTransactionsQuery.isLoading
              ? columns
              : columns.map(column => {
                  return {
                    ...column,
                    render: function renderPlaceholder() {
                      return <Skeleton key={column.key} title active={true} paragraph={false} />
                    }
                  }
                })
          }
          dataSource={
            !lockboxTransactionsQuery.isLoading
              ? transactions
              : ([...Array(2)].map((_, index) => ({
                  id: `key${index}`
                })) as unknown as LockboxTransaction[])
          }
          pagination={false}
        />
      </div>
    </div>
  )
}
